import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  MODE_CREDITS,
  STYLE_PRESETS,
  STYLE_PROFILES,
  type AmplifyResponse,
  type GenerationMode,
  type StylePreset,
  type Whale,
} from '@gilding/shared';
import { api } from '../api/client.js';
import { SceneIdeas } from './SceneIdeas.js';
import { trackProduct } from '../lib/productMetrics.js';
import { chainBeatScroll } from '../lib/beatScroll.js';
import { HouseScrub, HOUSE_BEATS, HOUSE_BEAT_CENTRE } from './HouseScrub.js';
import { Glass } from './Glass.js';
import { WhalePicker } from './WhalePicker.js';
import { FreeReferencePicker, type FreeRef } from './FreeReferencePicker.js';
import { cn } from '../lib/classnames.js';

/* =====================================================================
   NewGenerationScrub — /new as a scroll-scrubbed commissioning journey.

   The page is one continuous walk through the corridor (CorridorScrub),
   with six beats surfacing and receding as depth passes them:

     I.  Threshold  — "Bring a sketch." Two-line gilded headline.
     II. Character  — chosen whale enshrined in a gilt cartouche + picker.
     III. Sketch    — a commission plate; one line becomes a full brief.
     IV.  Look      — style presets as struck gilt coin medallions.
     V.   Shape     — aspect ratios as hanging gilt frames at true proportion.
     VI.  Strike    — the doorway of light; a gilded plate strikes the piece.

   A cross-cutting Commission Plate glyph hovers in the corner and
   progressively illuminates as each beat is answered — by the time the
   visitor reaches the Strike, the plate is a complete manuscript
   commission and it dissolves into the render ceremony.

   Every visual gesture reuses the Landing / Buy vocabulary — gild-line
   entrance, gild-word foil, gild-plate face, gild-rake sweep, foil-text
   accents, Halo + drop-shadow legibility, gild-numeral for beat markers,
   beat-rule that draws in with the reveal. Nothing new was minted for
   this page; every leaf came from the existing design system.

   Reduced-motion visitors get the parent's classic editorial column via
   a `Fallback` slot — the scrub itself is never mounted for them.
   ==================================================================== */

/* ---------- envelope math (mirrors Landing / Buy) ----------------- */

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function smooth(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** A rise-in, hold, recede envelope over [a,b] in, [c,d] out — returns 0→1. */
function band(p: number, a: number, b: number, c: number, d: number): number {
  if (p <= a || p >= d) return 0;
  if (p < b) return smooth(a, b, p);
  if (p > c) return 1 - smooth(c, d, p);
  return 1;
}

/**
 * Drive a beat overlay from scroll progress. Opacity + a small translate
 * + a small scale give the rack-focus feel; explicit rule: NO filter:blur
 * per frame. iOS Safari treats blur on a viewport-sized overlay as one of
 * the most expensive ops in the stack, and re-computing it on every scroll
 * tick was what read to the user as freezing / smearing. The rack-focus
 * still reads without it — the blur was a nice-to-have, not the effect.
 */
function driveBeat(
  el: HTMLDivElement | null,
  p: number,
  a: number,
  b: number,
  c: number,
  d: number,
  interactive = false,
): number {
  if (!el) return 0;
  const o = band(p, a, b, c, d);
  const dir = p < (b + c) / 2 ? 1 : -1;
  el.style.opacity = String(o);
  const scale = (0.97 + 0.03 * o).toFixed(3);
  el.style.transform = `translate3d(0, ${(14 * (1 - o) * dir).toFixed(1)}px, 0) scale(${scale})`;
  // filter:blur intentionally removed — see comment above.
  if (interactive) el.style.pointerEvents = o > 0.55 ? 'auto' : 'none';
  // The gilded numeral rule (if any) draws in lockstep with the beat.
  const rule = el.querySelector('.beat-rule');
  if (rule instanceof HTMLElement) rule.style.transform = `scaleX(${o.toFixed(3)})`;
  return o;
}

/** Light haptic on primary interactions. Android + Chrome/Firefox honour
 *  navigator.vibrate; iOS Safari silently no-ops (never throws). Kept as
 *  a whisper (8ms) — anything longer reads as a system notification. */
function tap(): void {
  try {
    navigator.vibrate?.(8);
  } catch {
    /* noop */
  }
}

/** Fade a headline out over the doors' hold (envelope for Threshold — the
 *  doors chamber holds until p≈0.136, so the headline clears just before
 *  the first transit rips). */
function driveHeadline(el: HTMLDivElement | null, p: number): number {
  if (!el) return 0;
  const o = clamp01(1 - smooth(0.055, 0.125, p));
  el.style.opacity = String(o);
  const scale = (0.97 + 0.03 * o).toFixed(3);
  el.style.transform = `translate3d(0, ${(-22 * (1 - o)).toFixed(1)}px, 0) scale(${scale})`;
  // No filter:blur — same iOS-cost reason as driveBeat.
  return o;
}

/** Scroll bands for each beat — imported from the HouseScrub chapter map so
 *  every beat surfaces over its chamber's settled hold and clears before the
 *  transit. One source of truth, shared with the rail/thread/plate. */
const BEATS = HOUSE_BEATS;

/* ---------- props ------------------------------------------------- */

export interface NewGenerationScrubProps {
  /** True on /new?remix=... — the source scene guides the gilding. */
  isRemix: boolean;
  /** Cover thumbnail of the remix source (rendered inside the character beat). */
  remixSourceThumb: string | null;
  /** True when a character (own whale OR uploaded/looked-up) is attached. */
  isKontext: boolean;
  /** Display name of the attached character, for headline + strike copy. */
  characterName: string | null;

  /* Character beat */
  showWhalePicker: boolean;
  whale: Whale | null;
  onWhaleChange: (w: Whale | null) => void;
  uploadedRef: FreeRef | null;
  onUploadedRefChange: (r: FreeRef | null) => void;

  /* Sketch beat */
  sketch: string;
  onSketchChange: (next: string) => void;
  suggestFlash: number;
  onSurprise: () => void;
  surprising: boolean;

  /* Look beat */
  stylePreset: StylePreset;
  onStyleChange: (next: StylePreset) => void;
  mode: GenerationMode;
  onModeChange: (next: GenerationMode) => void;

  /* Shape beat */
  aspectRatio: '1:1' | '3:2' | '2:3' | '16:9' | '9:16';
  onAspectChange: (next: '1:1' | '3:2' | '2:3' | '16:9' | '9:16') => void;

  /* Strike beat */
  onStrike: () => void;
  busy: boolean;
  waiting: boolean;
  recovering: boolean;
  recoverHint: boolean;
  errorMessage: string | null;
  outOfLeaves: boolean;
  jobBrief: AmplifyResponse | null;
  jobError: string | null;
}

/* ---------- the scrub -------------------------------------------- */

export function NewGenerationScrub(props: NewGenerationScrubProps) {
  const {
    isRemix,
    remixSourceThumb,
    isKontext,
    characterName,
    showWhalePicker,
    whale,
    onWhaleChange,
    uploadedRef,
    onUploadedRefChange,
    sketch,
    onSketchChange,
    suggestFlash,
    onSurprise,
    surprising,
    stylePreset,
    onStyleChange,
    mode,
    onModeChange,
    aspectRatio,
    onAspectChange,
    onStrike,
    busy,
    waiting,
    recovering,
    recoverHint,
    errorMessage,
    outOfLeaves,
    jobError,
  } = props;

  const headlineRef = useRef<HTMLDivElement>(null);
  const characterRef = useRef<HTMLDivElement>(null);
  const [moreCharacterOptions, setMoreCharacterOptions] = useState(false);
  useEffect(() => {
    const panel = characterRef.current;
    if (!panel) return;
    const update = () => setMoreCharacterOptions(panel.scrollHeight - panel.clientHeight - panel.scrollTop > 12);
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    if (panel.firstElementChild) observer.observe(panel.firstElementChild);
    panel.addEventListener('scroll', update, { passive: true });
    update();
    return () => { observer.disconnect(); panel.removeEventListener('scroll', update); };
  }, []);
  const sketchRef = useRef<HTMLDivElement>(null);
  const lookRef = useRef<HTMLDivElement>(null);
  const [moreLookOptions, setMoreLookOptions] = useState(false);
  useEffect(() => {
    const panel = lookRef.current;
    if (!panel) return;
    const update = () => setMoreLookOptions(panel.scrollHeight - panel.clientHeight - panel.scrollTop > 12);
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    if (panel.firstElementChild) observer.observe(panel.firstElementChild);
    panel.addEventListener('scroll', update, { passive: true });
    update();
    return () => { observer.disconnect(); panel.removeEventListener('scroll', update); };
  }, []);
  const shapeRef = useRef<HTMLDivElement>(null);
  const strikeRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLDivElement>(null);
  const colophonRef = useRef<HTMLDivElement>(null);

  // Which beat's number the commission plate should highlight.
  const [activeBeat, setActiveBeat] = useState(0);

  // Progress of each beat, used by the commission plate to know which
  // marginalia to ink in as depth passes (stable across re-renders via ref).
  const beatProgressRef = useRef<[number, number, number, number, number, number]>([
    0, 0, 0, 0, 0, 0,
  ]);
  // Whether each beat has been reached at least once (sticky — once inked, stays).
  const inkedRef = useRef<[boolean, boolean, boolean, boolean, boolean, boolean]>([
    false, false, false, false, false, false,
  ]);
  // Mirror of inkedRef for React consumers (rail/plate light up nodes).
  const [nodeStates, setNodeStates] = useState<boolean[]>([false, false, false, false, false, false]);
  const [, forcePlateTick] = useState(0);

  const handleProgress = (p: number): void => {
    driveHeadline(headlineRef.current, p);
    if (cueRef.current) {
      const o = clamp01(1 - smooth(0.02, 0.12, p));
      cueRef.current.style.opacity = String(o);
    }

    const o0 = driveHeadline(headlineRef.current, p);
    const o1 = driveBeat(
      characterRef.current, p, BEATS.character.a, BEATS.character.b, BEATS.character.c, BEATS.character.d, true,
    );
    const o2 = driveBeat(
      sketchRef.current, p, BEATS.sketch.a, BEATS.sketch.b, BEATS.sketch.c, BEATS.sketch.d, true,
    );
    const o3 = driveBeat(
      lookRef.current, p, BEATS.look.a, BEATS.look.b, BEATS.look.c, BEATS.look.d, true,
    );
    const o4 = driveBeat(
      shapeRef.current, p, BEATS.shape.a, BEATS.shape.b, BEATS.shape.c, BEATS.shape.d, true,
    );
    const o5 = driveBeat(
      strikeRef.current, p, BEATS.strike.a, BEATS.strike.b, BEATS.strike.c, BEATS.strike.d, true,
    );

    beatProgressRef.current = [o0, o1, o2, o3, o4, o5];
    const inked = inkedRef.current.slice() as [
      boolean, boolean, boolean, boolean, boolean, boolean,
    ];
    let inkChanged = false;
    beatProgressRef.current.forEach((v, i) => {
      if (!inked[i] && v > 0.5) {
        inked[i] = true;
        inkChanged = true;
      }
    });
    if (inkChanged) {
      inkedRef.current = inked;
      setNodeStates(inked.slice());
    }

    // Which beat's numeral the commission plate should highlight — whichever
    // is most visible right now.
    let bestIdx = 0;
    let bestV = -1;
    beatProgressRef.current.forEach((v, i) => {
      if (v > bestV) {
        bestV = v;
        bestIdx = i;
      }
    });
    if (bestIdx !== activeBeat) setActiveBeat(bestIdx);

    // Payoff on arrival: light up the strike plate for the gild-word gild.
    const strike = strikeRef.current;
    if (strike && o5 > 0.6 && !strike.classList.contains('is-arrived')) {
      strike.classList.add('is-arrived');
    }
    if (colophonRef.current) {
      colophonRef.current.style.opacity = String(clamp01(smooth(0.94, 1.0, p)));
    }
  };

  // If we somehow scroll before hydrating (deep-link), tick the plate at
  // least once so the corner glyph paints its initial state.
  useEffect(() => {
    const t = window.setTimeout(() => forcePlateTick((x) => x + 1), 60);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (busy) return;
    const cleanups = [characterRef, sketchRef, lookRef, shapeRef]
      .flatMap((ref) => ref.current ? [chainBeatScroll(ref.current)] : []);
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [busy]);

  // Once the gild is rendering, freeze the page on the altar loading scene. A
  // stray scroll must not carry the visitor back up through the other chambers
  // mid-render. overflow:hidden alone doesn't hold (Lenis / programmatic scroll
  // slip past it), so we pin the exact offset and snap back on any scroll,
  // while blocking wheel + single-finger touch so nothing fights the pin. Taps
  // (bottom nav) still fire — preventing touchmove doesn't cancel a tap.
  useEffect(() => {
    if (!busy) return;
    const lockedY = window.scrollY;
    const snapBack = (): void => {
      if (Math.abs(window.scrollY - lockedY) > 0.5) window.scrollTo(0, lockedY);
    };
    const blockWheel = (e: WheelEvent): void => e.preventDefault();
    const blockTouch = (e: TouchEvent): void => {
      if (e.touches.length === 1) e.preventDefault();
    };
    window.addEventListener('scroll', snapBack, { passive: true });
    window.addEventListener('wheel', blockWheel, { passive: false });
    window.addEventListener('touchmove', blockTouch, { passive: false });
    return () => {
      window.removeEventListener('scroll', snapBack);
      window.removeEventListener('wheel', blockWheel);
      window.removeEventListener('touchmove', blockTouch);
    };
  }, [busy]);

  // Jump-to-beat from the commission plate. The plate rows are click targets,
  // so the visitor can revisit any answered beat without hunting for it —
  // manuscript nav that reads as part of the piece, not a scrubber bar. Uses
  // a smooth scroll so it feels like guided walking, not a hard jump.
  // Jump targets = chamber hold centres, from the shared chapter map.
  const BEAT_CENTRE = HOUSE_BEAT_CENTRE;
  const onJump = (beat: keyof typeof BEAT_CENTRE): void => {
    // The scrub's outer <section> is our nearest section ancestor. From any
    // beat overlay ref (each is inside the sticky wrap, which is inside the
    // outer section), .closest('section') resolves reliably. Fallback: no-op.
    const anchor = characterRef.current;
    const section = anchor?.closest('section');
    if (!section) return;
    const r = section.getBoundingClientRect();
    const total = r.height - window.innerHeight;
    if (total <= 0) return;
    const sectionTop = window.scrollY + r.top;
    const target = sectionTop + BEAT_CENTRE[beat] * total;
    // Instant scroll — the corridor's own dispP smoother eases the picture in,
    // so layering native smooth-scroll on top double-eases and drags the wait
    // to several seconds. Instant + inertial catch-up reads as one glide.
    window.scrollTo({ top: target, behavior: 'auto' });
  };

  /* -------------------------------------------------------------- */

  // Which beats are filled — feeds the commission plate's inked marks.
  const filled: BeatFilled = {
    character: !!(whale || uploadedRef),
    sketch: sketch.trim().length >= 3,
    look: !!stylePreset,
    shape: !!aspectRatio,
  };

  const strikeCredits = MODE_CREDITS[mode];
  const strikeLabel = isRemix
    ? `Remix with ${characterName ?? 'my character'}`
    : `Gild ${isKontext ? (characterName ?? 'your character') : 'it'}`;

  // The gild loads IN PLACE on the altar — no page takeover. When busy, a
  // sheet of gold leaf slides down from the top of the viewport and settles
  // onto the copper plate, then shimmers while the piece renders. The parent
  // navigates to /result the moment the durable job reports done.
  return (
    <HouseScrub
      heightVh={560}
      onProgress={handleProgress}
      loading={busy}
      className="theme-dark z-0 ml-[calc(50%-50vw)] w-screen"
    >
      {/* --------------------------------------------------------------
          I. THRESHOLD — the entrance headline. Rises, holds, and dissolves
          as the camera walks into the hall.
          -------------------------------------------------------------- */}
      <div
        ref={headlineRef}
        className="commission-intro pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 pb-[7rem] pt-20 text-center will-change-[opacity,transform]"
      >
        <div className="relative max-w-3xl">
          <Halo width="150%" height="155%" />
          {/* Sized for the phone first: a smaller clamp than the Landing hero
              so the two lines never overflow a narrow viewport (they did at
              the h-hero 56-108px clamp). */}
          <h1 className="relative font-display font-bold text-balance text-ink drop-shadow-[0_3px_26px_rgba(0,0,0,0.95)] text-[clamp(2.35rem,1.3rem+5vw,4.75rem)] leading-[0.98] tracking-[-0.03em]">
            <span className="gild-line">
              <span className="gild-line__inner">
                {isRemix ? 'Remix this gilding.' : 'Bring a sketch.'}
              </span>
            </span>
            <span className="gild-line">
              <span className="gild-line__inner" style={{ animationDelay: '150ms' }}>
                {isRemix ? (
                  <>
                    Cast it in{' '}
                    <GildWord className="font-display italic">gold</GildWord>.
                  </>
                ) : (
                  <>
                    Commission it in{' '}
                    <GildWord className="font-display italic">gold</GildWord>.
                  </>
                )}
              </span>
            </span>
          </h1>
          <div className="gild-underline" aria-hidden="true" />
          <p className="relative mx-auto mt-5 max-w-md font-display text-[0.95rem] italic leading-relaxed text-ink/90 drop-shadow-[0_2px_16px_rgba(0,0,0,0.98)] sm:mt-6 sm:text-lede">
            {isRemix
              ? 'The scene comes from the source. Bring your character, and the House writes them in.'
              : 'One line is all it takes.'}
          </p>
          <div ref={cueRef} className="mt-4 flex justify-center will-change-[opacity] sm:mt-6">
            <p className="flex items-center gap-3 font-display text-caption italic tracking-[0.04em] text-ink/85 drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
              <ScrollGlyph /> Your commission begins
            </p>
          </div>
        </div>
      </div>

      {/* --------------------------------------------------------------
          II. CHARACTER — the whale or reference enshrined in a gilt
          cartouche. Picker sits beneath in a Glass panel.
          -------------------------------------------------------------- */}
      <div
        ref={characterRef}
        data-beat="character"
        data-lenis-prevent
        style={{
          opacity: 0,
          // Dark radial scrim behind the beat content — keeps gold-on-gold
          // legible when the corridor bloom takes over the backdrop. Inline
          // style because Tailwind's arbitrary-value parser silently rejects
          // nested rgba() inside `[background:...]`.
          background:
            'radial-gradient(65% 55% at 50% 45%, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.42) 55%, rgba(0,0,0,0.05) 85%)',
        }}
        className="house-beat-scroll absolute inset-0 z-10 flex items-start justify-center overflow-y-auto px-4 pt-24 pb-4 will-change-[opacity,transform]"
      >
        <div className="w-full max-w-3xl">
          <BeatHeader
            numeral="II."
            title="Your character"
            note="Set a whale on the plinth, or let the scene stand alone."
          />
          <CharacterBeat
            isRemix={isRemix}
            remixSourceThumb={remixSourceThumb}
            characterName={characterName}
            whale={whale}
            uploadedRef={uploadedRef}
            showWhalePicker={showWhalePicker}
            onWhaleChange={onWhaleChange}
            onUploadedRefChange={onUploadedRefChange}
          />
        </div>
      </div>

      {/* --------------------------------------------------------------
          III. SKETCH — one line, gilded into a full brief. Textarea
          sits inside a commission plate; as they type, an amplified
          brief materialises below (throttled Claude call).
          -------------------------------------------------------------- */}
      <div
        ref={sketchRef}
        data-beat="sketch"
        data-lenis-prevent
        style={{
          opacity: 0,
          // Dark radial scrim behind the beat content — keeps gold-on-gold
          // legible when the corridor bloom takes over the backdrop. Inline
          // style because Tailwind's arbitrary-value parser silently rejects
          // nested rgba() inside `[background:...]`.
          background:
            'radial-gradient(65% 55% at 50% 45%, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.42) 55%, rgba(0,0,0,0.05) 85%)',
        }}
        className="house-beat-scroll absolute inset-0 z-10 flex items-start justify-center overflow-y-auto px-4 pt-24 pb-4 will-change-[opacity,transform]"
      >
        <div className="w-full max-w-3xl">
          <BeatHeader
            numeral="III."
            title={isKontext ? `A scene for ${characterName ?? 'your character'}` : 'Your line'}
            note={
              isKontext
                ? 'Name a place. The House carries them into it.'
                : 'One line is enough. Claude illuminates the rest.'
            }
          />
          <SketchCommissionPlate
            onStyleChange={onStyleChange}
            isKontext={isKontext}
            whaleName={characterName}
            value={sketch}
            onChange={onSketchChange}
            onSurprise={onSurprise}
            surprising={surprising}
            flashToken={suggestFlash}
          />
        </div>
      </div>

      {/* --------------------------------------------------------------
          IV. LOOK — style presets as struck gilt coin medallions.
          -------------------------------------------------------------- */}
      <div
        ref={lookRef}
        data-beat="look"
        data-lenis-prevent
        style={{
          opacity: 0,
          // Dark radial scrim behind the beat content — keeps gold-on-gold
          // legible when the corridor bloom takes over the backdrop. Inline
          // style because Tailwind's arbitrary-value parser silently rejects
          // nested rgba() inside `[background:...]`.
          background:
            'radial-gradient(65% 55% at 50% 45%, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.42) 55%, rgba(0,0,0,0.05) 85%)',
        }}
        className="house-beat-scroll absolute inset-0 z-10 flex items-start justify-center overflow-y-auto px-4 pt-24 pb-4 will-change-[opacity,transform]"
      >
        <div className="w-full max-w-4xl">
          <BeatHeader
            numeral="IV."
            title="The look"
            note="Every style swims in the bath. Pull yours."
          />
          <LookMedallions value={stylePreset} onChange={onStyleChange} />
          <ModeRail value={mode} onChange={onModeChange} />
        </div>
      </div>

      {/* --------------------------------------------------------------
          V. SHAPE — aspect ratios as hanging gilt frames.
          -------------------------------------------------------------- */}
      <div
        ref={shapeRef}
        data-beat="shape"
        data-lenis-prevent
        style={{
          opacity: 0,
          // Dark radial scrim behind the beat content — keeps gold-on-gold
          // legible when the corridor bloom takes over the backdrop. Inline
          // style because Tailwind's arbitrary-value parser silently rejects
          // nested rgba() inside `[background:...]`.
          background:
            'radial-gradient(65% 55% at 50% 45%, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.42) 55%, rgba(0,0,0,0.05) 85%)',
        }}
        className="house-beat-scroll absolute inset-0 z-10 flex items-start justify-center overflow-y-auto px-4 pt-24 pb-4 will-change-[opacity,transform]"
      >
        <div className="w-full max-w-3xl">
          <BeatHeader
            numeral="V."
            title="The shape"
            note="How the frame is cut."
          />
          <ShapeGiltFrames value={aspectRatio} onChange={onAspectChange} />
        </div>
      </div>

      {/* --------------------------------------------------------------
          VI. STRIKE — the doorway of light. A gilded plate materialises;
          the button strikes the gilding.
          -------------------------------------------------------------- */}
      <div
        ref={strikeRef}
        data-beat="strike"
        style={{
          opacity: 0,
          // Bottom-weighted scrim: darkens the lower altar (where the button
          // sits) for legibility while leaving the copper plate + suspended
          // leaf up top clear to read as the scene's hero.
          background:
            'linear-gradient(180deg, transparent 34%, rgba(0,0,0,0.28) 58%, rgba(0,0,0,0.72) 100%)',
        }}
        data-lenis-prevent
        className="house-beat-scroll absolute inset-0 z-10 flex items-start justify-center overflow-y-auto px-4 pt-24 pb-4 will-change-[opacity,transform]"
      >
        {/* Hide the button while busy — StrikeLoading owns all messaging. */}
        {!busy && (
          <StrikePlate
            summary={`${characterName ?? 'From your sketch'} · ${STYLE_PROFILES[stylePreset].label} · ${aspectRatio} · ${mode === '4up' ? '4 variations' : mode === 'double' ? '2 variations' : mode === 'battle' ? '3 models' : '1 image'}`}
            strikeLabel={strikeLabel}
            strikeCredits={strikeCredits}
            onStrike={onStrike}
            busy={busy}
            waiting={waiting}
            recovering={recovering}
            canStrike={filled.sketch || isRemix}
          />
        )}
      </div>

      {/* In-place gild loading — a sheet of gold leaf descends from the top
          onto the copper plate and shimmers while the piece renders. Replaces
          the old full-page ceremony; the scene stays put. */}
      {busy && <StrikeLoading recovering={recovering} recoverHint={recoverHint} />}

      {/* The commission plate hovers in the corner throughout, filling in
          as beats resolve. Clicking any numeral scrolls to that beat's centre. */}
      <CommissionPlate
        activeBeat={activeBeat}
        filled={filled}
        stylePreset={stylePreset}
        aspectRatio={aspectRatio}
        credits={strikeCredits}
        characterUrl={whale?.imageUrl ?? uploadedRef?.url ?? null}
        onJump={onJump}
      />

      {/* Beat rail — the quick-nav. One tap moves between beats (no manual
          scrolling), and the leaf button skips straight to the Strike. Shown
          whenever the CommissionPlate can't be (portrait phones AND short
          landscape viewports); the plate takes over only on tall desktops. */}
      {((activeBeat === 1 && moreCharacterOptions) || (activeBeat === 3 && moreLookOptions)) && !busy && typeof document !== 'undefined' && createPortal(
        <button type="button" className="character-more-cue fixed left-1/2 z-40 -translate-x-1/2 rounded-full border border-accent/50 bg-[#18120d] px-4 py-2 font-display text-caption italic text-[#f4e7c8] shadow-lg"
          style={{ bottom: 'calc(11.5rem + env(safe-area-inset-bottom))' }}
          onClick={() => { const panel = activeBeat === 3 ? lookRef.current : characterRef.current; panel?.scrollBy({ top: panel.clientHeight * 0.7, behavior: 'smooth' }); }}>
          {activeBeat === 3 ? 'Scroll for variation count' : 'More character options'} <span aria-hidden="true">↓</span>
        </button>, document.body
      )}
      <BeatRail activeBeat={activeBeat} nodes={nodeStates} onJump={onJump} summary={`${characterName ?? 'From text'} · ${aspectRatio} · ${strikeCredits} ${strikeCredits === 1 ? 'leaf' : 'leaves'}`} />

      {/* Error toast — a fixed card at the foot when a gild fails outright
          (moderation, insufficient credits, etc). Mirrors Landing. */}
      {(errorMessage || jobError || recoverHint) && !busy && (
        <div className="fixed inset-x-0 z-50 flex justify-center px-5 bottom-[calc(6rem_+_env(safe-area-inset-bottom))] sm:bottom-6">
          <ToastCard
            errorMessage={errorMessage ?? jobError}
            outOfLeaves={outOfLeaves}
            recovering={false}
            recoverHint={recoverHint}
          />
        </div>
      )}

      {/* Foot colophon: fades in at the very end. */}
      <div
        ref={colophonRef}
        style={{ opacity: 0 }}
        className="pointer-events-none absolute inset-x-0 bottom-4 z-10 px-6 text-center will-change-[opacity]"
      >
        <p className="font-display italic text-caption text-ink/80 drop-shadow-[0_2px_14px_rgba(0,0,0,0.95)]">
          Gilding. Bring a sketch, leave with gold.
        </p>
      </div>
    </HouseScrub>
  );
}

/* =====================================================================
   StrikeLoading — the gild rendering, IN PLACE on the altar.

   Portalled full-viewport over the still-visible altar scene (no page
   takeover). On mount: a soft ignition flash, then a sheet of gold leaf
   slides down from the top of the viewport and settles onto the copper
   plate, blooming a warm glow as it lands, then shimmers while the piece
   renders. A quiet caption + filament progress sit at the foot. The
   parent navigates to /result the instant the durable job reports done;
   recovery/recoverHint just re-word the caption (phone-slept case).
   ==================================================================== */

function StrikeLoading({
  recovering,
  recoverHint,
}: {
  recovering: boolean;
  recoverHint: boolean;
}) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    try { navigator.vibrate?.([10, 30, 18]); } catch { /* noop */ }
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  const [showFlash, setShowFlash] = useState(true);
  useEffect(() => {
    const t = window.setTimeout(() => setShowFlash(false), 1100);
    return () => window.clearTimeout(t);
  }, []);

  // Honest asymptotic estimate — eases toward 94% and holds until done.
  const pct = Math.min(94, Math.round(100 * (1 - Math.exp(-seconds / 20))));
  const title = recovering ? 'Finding your gilding' : recoverHint ? 'Still finishing' : 'Gilding';
  const caption = recovering
    ? 'Your screen slept, but the gild kept working. Fetching it now.'
    : recoverHint
      ? 'It’s saving to your gallery. Give it a moment.'
      : `Laying the leaf. ${seconds}s · thirty to sixty seconds is normal.`;

  if (typeof document === 'undefined') return null;
  // The altar VIDEO loops beneath this (HouseScrub loading mode): the leaf
  // trembles over the glowing plate, embers breathe. This overlay is just an
  // elegant caption + progress at the foot — the living scene is the graphic,
  // not a pasted square.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] overflow-hidden"
      role="status"
      aria-live="polite"
      style={{ animation: 'fade-in 400ms ease-out both' }}
    >
      {/* Bottom scrim — darkens the lower quarter so the caption reads over the
          candles without hiding the plate + leaf above. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(180deg, transparent 58%, rgba(9,8,12,0.5) 78%, rgba(9,8,12,0.92) 100%)' }}
      />
      {/* Ignition flash (gold flake rain) on strike. */}
      {showFlash && <StrikeIgnition />}

      {/* Foot: fleuron + title + caption + a gilded filament progress with a
          faceted lozenge riding its tip. Sits above the app nav. */}
      <div
        className="absolute inset-x-0 flex flex-col items-center gap-2.5 px-8 text-center"
        style={{ bottom: 'calc(7.5rem + env(safe-area-inset-bottom))' }}
      >
        <FleuronMark small />
        <p className="font-display text-h-display italic leading-none text-ink drop-shadow-[0_2px_18px_rgba(0,0,0,0.95)]">
          {title}
          <span className="loading-ellipsis" aria-hidden>…</span>
        </p>
        <p className="max-w-xs font-display italic text-caption text-ink/85 drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
          {caption}
        </p>
        {/* gilded filament progress with a lozenge tip */}
        <div className="relative mt-3 h-4 w-60 max-w-full" aria-hidden>
          <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-ink/20" />
          <span
            className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 rounded-full transition-[width] duration-1000 ease-linear"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg,#9a722b,#e9c873 60%,#fff6da)',
              boxShadow: '0 0 10px rgba(233,200,115,0.6)',
            }}
          />
          <span
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 transition-[left] duration-1000 ease-linear"
            style={{ left: `${pct}%` }}
          >
            <svg viewBox="0 0 28 16" className="h-3.5 w-6 drop-shadow-[0_0_6px_rgba(233,200,115,0.7)]">
              <defs>
                <linearGradient id="loadLoz" x1="0" y1="0" x2="0.25" y2="1">
                  <stop offset="0" stopColor="#a87f2c" />
                  <stop offset="0.42" stopColor="#e9c873" />
                  <stop offset="0.52" stopColor="#fff3ce" />
                  <stop offset="0.64" stopColor="#c2922f" />
                  <stop offset="1" stopColor="#7d521a" />
                </linearGradient>
              </defs>
              <path d="M2 8 L14 3.2 L26 8 L14 12.8 Z" fill="url(#loadLoz)" />
              <circle cx="14" cy="8" r="1.1" fill="#fff3ce" />
            </svg>
          </span>
        </div>
        <p className="mt-1.5 font-mono text-[0.62rem] uppercase tracking-[0.24em] text-ink/50">
          Keep this page open
        </p>
      </div>
    </div>,
    document.body,
  );
}

function StrikeIgnition() {
  // 24 flakes cascading from the top of the viewport with per-flake random
  // horizontal drift. Below them, a full-screen radial gold flash that
  // peaks at ~250ms then dissolves. The whole sequence is 1.1s and
  // unmounts after. On motion-reduce the layer stays but is invisible via
  // CSS. No hard cuts anywhere — the theater beneath fades up as this
  // fades down, so the visitor sees one continuous "strike" moment.
  const flakes = Array.from({ length: 24 }, (_, i) => ({
    left: `${(i * 137) % 100}%`,
    delay: (i * 33) % 400,
    dur: 900 + (i * 47) % 240,
    rot: (i * 91) % 360,
    scale: 0.7 + ((i * 13) % 60) / 100,
  }));
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[80] motion-reduce:hidden"
      style={{ overflow: 'hidden' }}
    >
      {/* Radial flash — the "strike" burst. */}
      <span
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(closest-side, rgba(255,246,218,0.9) 0%, rgba(233,200,115,0.45) 30%, rgba(154,114,43,0.15) 55%, transparent 78%)',
          opacity: 0,
          animation: 'strike-flash 900ms cubic-bezier(0.16,0.68,0.2,1) both',
          mixBlendMode: 'screen',
        }}
      />
      {/* Falling gold flakes — the leaf being struck onto the plate. */}
      {flakes.map((f, i) => (
        <span
          key={i}
          className="absolute -top-6"
          style={{
            left: f.left,
            width: 6,
            height: 12,
            background: 'linear-gradient(180deg,#fff6da,#e9c873 42%,#9a722b)',
            borderRadius: 1,
            transform: `rotate(${f.rot}deg) scale(${f.scale})`,
            opacity: 0,
            animation: `strike-flake ${f.dur}ms cubic-bezier(0.3,0.6,0.2,1) ${f.delay}ms both`,
          }}
        />
      ))}
    </div>
  );
}

/* =====================================================================
   Beat header — the numeral + rule + title + note. Every beat carries
   this so the reader always knows which of the six passes they’re in.
   ==================================================================== */

function BeatHeader({
  numeral,
  title,
  note,
}: {
  numeral: string;
  title: string;
  note?: string;
}) {
  return (
    <header className="mb-6 flex flex-col items-center gap-2 text-center">
      <p className="gild-numeral font-display text-h-display italic leading-none">
        {numeral}
      </p>
      <span
        aria-hidden="true"
        className="beat-rule mx-auto mt-1 block h-px w-14 rounded-full"
        style={{ background: 'linear-gradient(90deg,transparent,#e9c873,transparent)' }}
      />
      <h2 className="heading-section mt-2 text-ink drop-shadow-[0_3px_24px_rgba(0,0,0,0.95)]">
        {title}
      </h2>
      {note && (
        <p className="max-w-lg font-display italic text-caption text-ink/85 drop-shadow-[0_2px_14px_rgba(0,0,0,0.95)]">
          {note}
        </p>
      )}
    </header>
  );
}

/* =====================================================================
   Character beat — a gilt cartouche holds the current character; a
   Glass panel below carries the pickers. Empty = an empty gilt frame
   with a whispered "optional".
   ==================================================================== */

function CharacterBeat({
  isRemix,
  remixSourceThumb,
  characterName,
  whale,
  uploadedRef,
  showWhalePicker,
  onWhaleChange,
  onUploadedRefChange,
}: {
  isRemix: boolean;
  remixSourceThumb: string | null;
  characterName: string | null;
  whale: Whale | null;
  uploadedRef: FreeRef | null;
  showWhalePicker: boolean;
  onWhaleChange: (w: Whale | null) => void;
  onUploadedRefChange: (r: FreeRef | null) => void;
}) {
  const [sheet, setSheet] = useState<null | 'pod' | 'lookup'>(null);
  const currentUrl = whale?.imageUrl ?? uploadedRef?.url ?? null;
  const label = characterName ?? '';
  const hasCharacter = !!currentUrl;
  const displayed = currentUrl ?? (isRemix ? remixSourceThumb : null);

  return (
    <div className="char-stack flex flex-col items-center gap-5">
        {showWhalePicker && (
          <button
            type="button"
            className="btn-primary btn-cta char-pod-action"
            onClick={() => setSheet('pod')}
          >
            {hasCharacter && whale ? 'Change character' : 'Browse your pod'}
            <span aria-hidden="true">→</span>
          </button>
        )}

      {/* The character as an apparition floating above the chamber's own
          plinth cushion — in the scene, not over it. The top margin drops
          it into the cushion zone of the video (lower-middle of the frame)
          so it reads as hovering over the velvet, not pinned to the header.
          Renders nothing while unassigned: the empty cushion IS the empty
          state. */}
      <div className="char-cartouche mt-[7svh] flex flex-col items-center gap-4 sm:mt-[3svh]">
        <Cartouche src={displayed} label={label} />
      </div>

      {/* Name + token seal — a persistent, unambiguous acknowledgement of
          the pick. If the character is a wallet-owned whale, the seal shows
          "#N"; if uploaded, "your upload". Empty: whispered "Optional". */}
      {hasCharacter ? (
        <div className="flex flex-wrap justify-center items-center gap-2" role="status">
          <span className="character-check" aria-label="Current character">✓</span>
          <p className="font-display text-h-meta italic leading-none text-ink drop-shadow-[0_2px_14px_rgba(0,0,0,0.9)]">
            {label}
          </p>
          {whale && <span className="font-display text-caption text-accent-soft">#{whale.tokenId}</span>}
 </div>
      ) : (
        <p className="max-w-xs text-center font-display italic text-caption text-ink/70 drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]">
          The plinth stands empty. Set a whale upon it, or gild the scene alone.
        </p>
      )}

      {/* Actions — one primary, two secondaries. The picker itself lives
          in a bottom sheet so the beat can breathe on mobile. */}
      <div className="char-actions flex flex-col items-center gap-3">
        <div className="flex flex-wrap justify-center items-center gap-4 font-display text-caption italic text-ink/70">
          <button
            type="button"
            className="underline-offset-4 transition-colors hover:text-ink hover:underline"
            onClick={() => setSheet('lookup')}
          >
            Look up any whale
          </button>
          <span aria-hidden className="h-3 w-px bg-hairline" />
          <button
            type="button"
            className="underline-offset-4 transition-colors hover:text-ink hover:underline"
            onClick={() => setSheet('lookup')}
          >
            Upload an image
          </button>
        </div>
        {hasCharacter && (
          <button
            type="button"
            className="btn-ghost mt-1"
            onClick={() => {
              onWhaleChange(null);
              onUploadedRefChange(null);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* The pod-browser sheet — full WhalePicker (search, sort, spotlight). */}
      {sheet === 'pod' && showWhalePicker && (
        <BottomSheet
          title="Your pod"
          subtitle="Tap a portrait to cast it into the scene."
          onClose={() => setSheet(null)}
        >
          <WhalePicker
            selected={whale}
            onChange={(w) => {
              onWhaleChange(w);
              if (w) onUploadedRefChange(null);
              setSheet(null);
            }}
          />
        </BottomSheet>
      )}

      {/* The lookup / upload sheet. */}
      {sheet === 'lookup' && (
        <BottomSheet
          title="Look up a character"
          subtitle="Any Whacky Whale by number, or upload the image you want in the scene."
          onClose={() => setSheet(null)}
        >
          <FreeReferencePicker
            value={uploadedRef}
            onChange={(r) => {
              onUploadedRefChange(r);
              if (r) onWhaleChange(null);
              if (r) setSheet(null);
            }}
            uploadFn={api.uploadReference}
          />
        </BottomSheet>
      )}
    </div>
  );
}

/** A carved gilt cartouche — a large rounded gold-rimmed portrait frame.
 *  Sized generously so it reads as the beat's anchor. Empty state renders
 *  a fleuron ornament inside the bezel. */
/**
 * Cartouche — the character as an APPARITION IN THE SCENE, not UI over it.
 * The rotunda chamber already stages this beat (an empty velvet cushion on
 * a moonlit plinth), so the portrait floats just above it: soft-feathered
 * edges (no plate, no rim), a warm under-glow kissing the cushion, a slow
 * levitation bob, and gold dust rising around it. Pointer/gyro still tilt
 * it gently — a hologram materialised in the oculus beam. Empty state
 * renders NOTHING: the scene's own waiting cushion is the empty state.
 */
function Cartouche({ src, label }: { src: string | null; label: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Cast flourish + haptic when a character lands on the plinth.
  const [castKey, setCastKey] = useState(0);
  const prevSrcRef = useRef<string | null>(src);
  useEffect(() => {
    const prev = prevSrcRef.current;
    prevSrcRef.current = src;
    if (!prev && src) {
      setCastKey((k) => k + 1);
      try { navigator.vibrate?.(18); } catch { /* noop */ }
    }
  }, [src]);

  // Pointer + gyroscope tilt — kept subtle (an apparition sways; it doesn't
  // wobble). The levitation bob rides a CSS keyframe on the INNER element so
  // the two transforms never fight.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !src) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    let tx = 0, ty = 0;
    let cx = 0, cy = 0;
    let raf = 0;
    const apply = (): void => {
      raf = requestAnimationFrame(apply);
      cx += (tx - cx) * 0.1;
      cy += (ty - cy) * 0.1;
      el.style.transform = `perspective(800px) rotateX(${(-cy * 5).toFixed(2)}deg) rotateY(${(cx * 7).toFixed(2)}deg)`;
    };
    raf = requestAnimationFrame(apply);

    const onPointer = (e: PointerEvent): void => {
      const r = el.getBoundingClientRect();
      tx = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width * 1.2)));
      ty = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height * 1.2)));
    };
    const onLeave = (): void => { tx = 0; ty = 0; };
    const onGyro = (e: DeviceOrientationEvent): void => {
      if (e.beta != null) ty = Math.max(-1, Math.min(1, (e.beta - 45) / 30));
      if (e.gamma != null) tx = Math.max(-1, Math.min(1, e.gamma / 30));
    };
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerleave', onLeave, { passive: true });
    window.addEventListener('deviceorientation', onGyro, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('deviceorientation', onGyro);
    };
  }, [src]);

  // No character → no element at all. The chamber's empty cushion IS the
  // empty state; covering it with an "unassigned" plate was the overlay
  // feeling this replaced.
  if (!src) return null;

  return (
    <div className="relative" style={{ perspective: '900px' }}>
      <div ref={wrapRef} style={{ transformStyle: 'preserve-3d' }}>
        {/* The bob carrier — levitates the whole apparition on a slow breath
            (CSS keyframe; motion-reduce stills it). */}
        <div className="apparition-bob relative">
          {/* Warm under-glow — the light the framed portrait casts DOWN onto
              the cushion. Blurred wide, screen-blended so it brightens the
              video's velvet rather than painting on it. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 -bottom-9 -translate-x-1/2 mix-blend-screen"
            style={{
              width: '140%',
              height: '4rem',
              background: 'radial-gradient(50% 100% at 50% 0%, rgba(233,200,115,0.6), rgba(233,200,115,0.2) 55%, transparent 80%)',
              filter: 'blur(12px)',
            }}
          />
          {/* The portrait, in a REAL ornate gilt frame that matches the
              rotunda's own wall frames: clean gilded edges, a thin dark mat,
              a deep drop-shadow so it reads as a framed object hovering in
              the beam, not a decal. */}
          <div
            className="gilt-frame relative block"
            style={{
              ['--gilt-w' as string]: 'clamp(16px, 4.6vw, 26px)',
              width: 'min(52vw, 12.5rem)',
              aspectRatio: '1 / 1',
              filter: 'drop-shadow(0 14px 32px rgba(0,0,0,0.62))',
            }}
          >
            <img
              src={src}
              alt={label || 'Selected character'}
              className="block h-full w-full object-cover"
              style={{ boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.55)' }}
            />
          </div>
          {/* Rising gold dust — three motes climbing past the frame, like the
              beam's dust catching its light. */}
          <span aria-hidden className="apparition-dust absolute left-[10%] bottom-2 h-1 w-1 rounded-full bg-accent-soft" />
          <span aria-hidden className="apparition-dust absolute left-[88%] bottom-8 h-[3px] w-[3px] rounded-full bg-accent-soft" style={{ animationDelay: '1.4s' }} />
          <span aria-hidden className="apparition-dust absolute left-[52%] -bottom-1 h-[3px] w-[3px] rounded-full bg-accent-soft" style={{ animationDelay: '2.6s' }} />
          {/* Cast flourish — flakes converge as the character materialises. */}
          {castKey > 0 && <CastFlourish key={castKey} />}
        </div>
      </div>
    </div>
  );
}

function CastFlourish() {
  // 16 flakes, deterministic angles + delays so it's the same "spell" every
  // time. Each flake starts far out (240px radial) and flies to centre with
  // a slight rotation. A radial flash follows 400ms in. The whole thing
  // resolves in ~900ms.
  const flakes = Array.from({ length: 16 }, (_, i) => {
    const angle = (i / 16) * Math.PI * 2 + 0.15;
    const dist = 220 + (i % 3) * 60;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const delay = (i * 22) % 260;
    return { dx, dy, delay, rot: (i * 47) % 360 };
  });
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 motion-reduce:hidden"
      style={{ overflow: 'visible' }}
    >
      {flakes.map((f, i) => (
        <span
          key={i}
          className="absolute left-1/2 top-1/2"
          style={{
            width: 6,
            height: 2,
            background: 'linear-gradient(90deg,#fff6da,#e9c873 40%,#9a722b)',
            borderRadius: 1,
            transform: `translate(${f.dx}px, ${f.dy}px) rotate(${f.rot}deg)`,
            opacity: 0,
            animation: `cast-flake 820ms cubic-bezier(0.2,0.7,0.2,1) ${f.delay}ms both`,
          }}
        />
      ))}
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(closest-side, rgba(255,246,218,0.65), rgba(233,200,115,0.25) 55%, transparent 80%)',
          opacity: 0,
          mixBlendMode: 'screen',
          animation: 'cast-flash 640ms ease-out 380ms both',
        }}
      />
    </span>
  );
}

/* =====================================================================
   BottomSheet — a modal that slides up from the bottom on mobile and
   centres as a card on desktop. Tap outside dismisses; Esc dismisses.
   Uses the site's Glass surface for the sheet body so the ornament
   language stays consistent. Rendered inline (no portal) — works for
   this use because the scrub root is a direct child of <main>.
   ==================================================================== */

function BottomSheet({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  // Track the visible-viewport height. iOS Safari's `svh` unit is static and
  // doesn't shrink when the software keyboard slides up — the sheet's body
  // ends up hidden behind the keyboard. `visualViewport` gives us the real
  // available height (keyboard subtracted). Fallback: `100dvh`, which tracks
  // the URL bar but not the keyboard.
  const [vv, setVv] = useState<number | null>(null);
  useEffect(() => {
    const vp = window.visualViewport;
    if (!vp) return;
    const update = (): void => setVv(vp.height);
    update();
    vp.addEventListener('resize', update);
    vp.addEventListener('scroll', update);
    return () => {
      vp.removeEventListener('resize', update);
      vp.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Sheet height computed against the visible viewport when we have it, so
  // the keyboard doesn't ever eat the body. 90% max, capped at 720px.
  const sheetMaxH = vv ? `${Math.min(vv * 0.92, 720)}px` : 'min(88dvh, 720px)';
  const bodyMaxH = vv ? `${Math.max(vv * 0.92 - 80, 200)}px` : 'calc(88dvh - 5rem)';

  // Track visualViewport OFFSETS too — iOS Safari sometimes reports the
  // page as scrolled below the layout viewport when the keyboard shifts.
  // Positioning the sheet with an explicit top-offset (rather than
  // `fixed inset-0`) keeps it pinned exactly where the visitor can see.
  const [vvOffset, setVvOffset] = useState({ top: 0, left: 0 });
  useEffect(() => {
    const vp = window.visualViewport;
    if (!vp) return;
    const sync = (): void => setVvOffset({ top: vp.offsetTop, left: vp.offsetLeft });
    sync();
    vp.addEventListener('resize', sync);
    vp.addEventListener('scroll', sync);
    return () => {
      vp.removeEventListener('resize', sync);
      vp.removeEventListener('scroll', sync);
    };
  }, []);

  // Portal to document.body so the sheet escapes any ancestor stacking
  // context (the CorridorScrub's sticky wrapper roots position:fixed on
  // iOS Safari via its transform composite layer, which is what was
  // clipping the sheet under the browser dock).
  if (typeof document === 'undefined') return null;
  const sheet = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      className="fixed z-[70] flex items-end justify-center bg-ink/80 sm:items-center"
      style={{
        // Explicit positioning against the LIVE visible viewport rather than
        // `inset-0`. On iOS Safari, `fixed inset-0` includes the strip UNDER
        // Safari's floating chrome + the software keyboard, which pushed the
        // sheet's bottom edge behind them. This pins the overlay to the
        // exact rectangle the visitor can see, no matter what iOS is doing.
        top: `${vvOffset.top}px`,
        left: `${vvOffset.left}px`,
        width: vv ? `${window.visualViewport?.width ?? window.innerWidth}px` : '100vw',
        height: vv ? `${vv}px` : '100dvh',
        animation: 'fade-in 180ms ease-out both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl overflow-hidden rounded-t-2xl border-t border-hairline-strong bg-surface-elevated shadow-2xl sm:mb-6 sm:rounded-2xl sm:border"
        style={{
          maxHeight: sheetMaxH,
          paddingBottom: 'env(safe-area-inset-bottom)',
          // Transform-only animation (no filter, no backdrop-blur on parent)
          // — cheap on iOS Safari, still reads as a rise.
          animation: 'sheet-rise 240ms cubic-bezier(0.2,0.7,0.2,1) both',
        }}
      >
        {/* Grabber — a small pill at the very top on mobile, the manuscript
            equivalent of a drawer handle. Desktop hides it. */}
        <div
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-hairline sm:hidden"
        />
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-hairline/50 px-5 py-3">
          <div className="flex min-w-0 flex-col">
            <h2 className="heading-section truncate">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 truncate font-display italic text-caption text-ink-subtle">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn-ghost shrink-0"
          >
            Done
          </button>
        </div>

        {/* Body — scrolls internally; sized against the LIVE viewport so
            the keyboard, URL bar, and safe area all coexist. */}
        <div
          className="overflow-y-auto px-5 py-4"
          style={{ maxHeight: bodyMaxH, WebkitOverflowScrolling: 'touch' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
  return createPortal(sheet, document.body);
}

/* =====================================================================
   Sketch commission plate — a Glass plate carrying the textarea, with
   Surprise-me, example chips (while empty), and a live GildedBrief
   preview beneath that fires a throttled Claude amplify as they type.
   ==================================================================== */

const SKETCH_MAX = 600;

const SKETCH_EXAMPLES: ReadonlyArray<{ label: string; text: string; style: StylePreset }> = [
  { label: 'Neon ramen, midnight', text: 'A neon-soaked ramen stall in the rain, Tokyo at midnight', style: 'photoreal-cinematic' },
  { label: 'Diver and whale', text: 'A free-diver drifting beside a sleeping humpback in clear blue water', style: 'photoreal-cinematic' },
  { label: 'Sunken cathedral', text: 'Bioluminescent jellyfish drifting through a sunken cathedral', style: '3d-render' },
  { label: 'Lighthouse keeper', text: 'A lone lighthouse keeper reading by candlelight, oil painting', style: 'painterly' },
  { label: 'Samurai cat', text: 'A samurai cat duels a thunder dragon at dawn', style: 'anime-leaning' },
  { label: 'Poster: Saturn', text: 'A retro travel poster for a city on the rings of Saturn', style: 'logo-poster' },
];

function SketchCommissionPlate({
  onStyleChange,
  isKontext,
  whaleName,
  value,
  onChange,
  onSurprise,
  surprising,
  flashToken,
}: {
  onStyleChange: (style: StylePreset) => void;
  isKontext: boolean;
  whaleName: string | null;
  value: string;
  onChange: (next: string) => void;
  onSurprise: () => void;
  surprising: boolean;
  flashToken: number;
}) {
  const fieldId = useId();
  const placeholder = isKontext
    ? `On the surface of the moon, earth rising, cinematic. (${whaleName ?? 'your character'} in frame.)`
    : 'An elder dragon emerging from a kelp forest, cinematic.';

  return (
    <div className="flex flex-col gap-4">
      <Glass className="relative overflow-hidden px-5 py-5 sm:px-7 sm:py-6" interactive={false}>
        {/* Fleuron on top of the plate — the manuscript tell. */}
        <div className="mb-3 flex items-center justify-center gap-3">
          <span aria-hidden className="h-px w-8 bg-gradient-to-r from-transparent to-accent/70" />
          <FleuronMark small />
          <span aria-hidden className="h-px w-8 bg-gradient-to-l from-transparent to-accent/70" />
        </div>
        <label htmlFor={fieldId} className="sr-only">
          Your sketch
        </label>
        <textarea
          id={fieldId}
          key={flashToken}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={SKETCH_MAX}
          rows={3}
          placeholder={placeholder}
          spellCheck
          className={cn(
            'field w-full resize-none bg-transparent font-display text-lede text-ink placeholder:text-ink/40',
            flashToken > 0 && 'field-flash',
          )}
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="font-mono text-[0.68rem] text-ink/55">
            {value.length}/{SKETCH_MAX}
          </p>
          <button
            type="button"
            onClick={onSurprise}
            disabled={surprising}
            className="btn-ghost"
            aria-label="Generate a random scene suggestion"
          >
            {surprising ? 'Rolling…' : 'Surprise me'}
          </button>
        </div>

        {/* Example chips while empty — a guided first taste. */}
        <SceneIdeas character={isKontext ? whaleName ?? 'your character' : null} hasDraft={!!value.trim()} onUse={(text, style) => { onChange(text); onStyleChange(style); }} />
        {!isKontext && value.trim().length === 0 && (
          <details className="mt-4"><summary className="cursor-pointer font-display text-caption">More scene ideas</summary><div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-2">
            <span className="font-display italic text-caption text-ink/60">Or start from:</span>
            {SKETCH_EXAMPLES.map((ex) => (
              <button
                key={ex.label}
                type="button"
                onClick={() => { onChange(ex.text); onStyleChange(ex.style); }}
                className="tag font-display italic text-caption text-ink/80 transition-colors duration-150 hover:text-ink"
                title={ex.text}
              >
                <span className="tag-dot" />
                {ex.label}
              </button>
            ))}
          </div></details>
        )}
      </Glass>

      {/* A whispered line under the plate — reassures they don’t need to
          write a full brief; Claude will handle it. The full amplified
          brief materialises during the actual gild via `jobBrief`. */}
      <p className="text-center font-display italic text-caption text-ink/60">
        {value.trim().length >= 3
          ? 'Claude will amplify your line into a two-hundred-word brief.'
          : 'Just a line is enough. Claude does the rest.'}
      </p>
    </div>
  );
}

/* =====================================================================
   Look medallions — style presets as struck gilt coins with a rake
   sweep on hover + a struck-numeral seal at the foot of the selected.
   ==================================================================== */

function LookMedallions({
  value,
  onChange,
}: {
  value: StylePreset;
  onChange: (next: StylePreset) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Pick a look"
      className="mt-1 grid grid-cols-4 gap-x-1.5 gap-y-2 sm:mt-2 sm:grid-cols-4 sm:gap-4 lg:grid-cols-7"
    >
      {STYLE_PRESETS.map((id, i) => {
        const active = id === value;
        const p = STYLE_PROFILES[id];
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => { tap(); onChange(id); }}
            className={cn(
              'group flex flex-col items-center gap-1.5 rounded-md border border-transparent px-1 py-2 text-center transition-transform duration-200 ease-out hover:-translate-y-0.5 sm:px-2 sm:py-3',
              active && 'bg-accent-glow/10',
            )}
          >
            <Medallion active={active} index={i + 1}>
              <StyleGlyph id={id} active={active} />
            </Medallion>
            <span
              className={cn(
                'font-display text-[0.62rem] leading-tight text-ink/85 transition-colors sm:text-caption',
                active && 'font-semibold text-ink',
              )}
            >
              {p.label}
            </span>
            <span className="hidden font-display italic text-[0.7rem] text-ink/60 sm:block">
              {p.oneLiner}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Medallion({
  active,
  index,
  children,
}: {
  active: boolean;
  index: number;
  children: ReactNode;
}) {
  return (
    <span className={cn('relative flex h-[3.1rem] w-[3.1rem] items-center justify-center sm:h-16 sm:w-16', active && 'is-arrived')}>
      <span
        aria-hidden
        className="absolute -inset-2 rounded-full bg-[radial-gradient(closest-side,rgba(233,200,115,0.28),transparent)] opacity-70 group-hover:opacity-100"
      />
      {/* Coin rim — a conic gold gradient so the highlight travels the edge. */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'conic-gradient(from 218deg,#6e4814,#e9c873 16%,#fff6da 29%,#c2922f 50%,#6e4814 68%,#e9c873 86%,#6e4814)',
          boxShadow: '0 8px 22px -8px rgba(0,0,0,0.8), inset 0 1px 1px rgba(255,255,255,0.55)',
        }}
      />
      {/* Embossed dark face + inner gold hairline. */}
      <span
        aria-hidden
        className="absolute inset-[3px] rounded-full"
        style={{
          background: 'radial-gradient(125% 125% at 38% 26%,#1b1626,#0a0810 72%)',
          boxShadow: 'inset 0 0 0 1px rgba(233,200,115,0.24), inset 0 2px 7px rgba(0,0,0,0.72)',
        }}
      />
      {/* Rake sweep on hover — reuses the gild-rake vocabulary from Landing. */}
      <span
        aria-hidden
        className={cn(
          'gild-rake pointer-events-none absolute inset-0 rounded-full',
          active && 'is-arrived',
        )}
      />
      {/* Polished specular. */}
      <span
        aria-hidden
        className="absolute left-[24%] top-[18%] h-2 w-3.5 rounded-full"
        style={{ background: 'radial-gradient(closest-side,rgba(255,255,255,0.55),transparent)', filter: 'blur(1px)' }}
      />
      <span className="relative text-[#f3dca0]">{children}</span>
      {active && (
        <span
          className="absolute -bottom-2 left-1/2 flex h-5 min-w-5 -translate-x-1/2 items-center justify-center rounded-full px-1.5"
          style={{
            background: 'linear-gradient(180deg,#fff6da,#e9c873 42%,#bd8c2c)',
            boxShadow: '0 2px 7px -1px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.7)',
          }}
        >
          <span className="font-display text-[0.7rem] font-semibold not-italic leading-none text-[#3a2708]">
            {toRoman(index)}
          </span>
        </span>
      )}
    </span>
  );
}

function StyleGlyph({ id, active }: { id: StylePreset; active: boolean }) {
  const stroke = active ? '#fff6da' : '#e9c873';
  const fill = active ? '#fff6da' : '#e9c873';
  const line = { fill: 'none', stroke, strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const common = { viewBox: '0 0 28 28', 'aria-hidden': true as const, width: 24, height: 24 };
  switch (id) {
    case 'photoreal-cinematic':
      return (
        <svg {...common}>
          <rect x="3" y="8.5" width="22" height="14.5" rx="2.5" {...line} />
          <path d="M9 8.5 L10.8 5.5 H17.2 L19 8.5" {...line} />
          <circle cx="14" cy="15.5" r="4.6" {...line} />
          <circle cx="14" cy="15.5" r="1.7" fill={fill} />
        </svg>
      );
    case 'editorial-illustration':
      return (
        <svg {...common}>
          <rect x="6.5" y="3.5" width="15" height="21" rx="2" {...line} />
          <circle cx="14" cy="11" r="4.2" fill={fill} />
          <path d="M9.5 18 H18.5 M9.5 21 H16" {...line} />
        </svg>
      );
    case 'anime-leaning':
      return (
        <svg {...common}>
          <path d="M13 4 C 13.8 9.5 14.5 10.2 20 11 C 14.5 11.8 13.8 12.5 13 18 C 12.2 12.5 11.5 11.8 6 11 C 11.5 10.2 12.2 9.5 13 4 Z" fill={fill} />
        </svg>
      );
    case '3d-render':
      return (
        <svg {...common}>
          <path d="M14 3.5 L22.5 8 L14 12.5 L5.5 8 Z" fill={fill} fillOpacity="0.55" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M5.5 8 L14 12.5 L14 22.5 L5.5 18 Z" {...line} />
          <path d="M22.5 8 L14 12.5 L14 22.5 L22.5 18 Z" {...line} />
        </svg>
      );
    case 'painterly':
      return (
        <svg {...common}>
          <path d="M20 4.5 L23.5 8 L15 16.5 L11.5 13 Z" {...line} />
          <path d="M11.5 13 L15 16.5 C 13 18.5 8.5 19.5 5 19 C 6.5 16 9 14.5 11.5 13 Z" fill={fill} />
        </svg>
      );
    case 'logo-poster':
      return (
        <svg {...common}>
          <text x="14" y="20" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontSize="18" fontWeight="600" fontStyle="italic" fill={stroke}>
            Aa
          </text>
        </svg>
      );
    case 'experimental':
      return (
        <svg {...common}>
          <path d="M11 4 H17" {...line} />
          <path d="M12.7 4 V10.5 L7 20.5 A2 2 0 0 0 9 23.5 H19 A2 2 0 0 0 21 20.5 L15.3 10.5 V4" {...line} />
          <path d="M9.3 17.5 H18.7" fill="none" stroke={fill} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
  }
}

function toRoman(n: number): string {
  const map: Array<[number, string]> = [
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  let x = n;
  for (const [v, s] of map) {
    while (x >= v) {
      out += s;
      x -= v;
    }
  }
  return out;
}

/* ---------- Mode rail: single | double | 4up (battle mode removed) -------- */

function ModeRail({
  value,
  onChange,
}: {
  value: GenerationMode;
  onChange: (next: GenerationMode) => void;
}) {
  const modes: ReadonlyArray<{ id: GenerationMode; label: string; hint: string }> = [
    { id: 'single', label: 'One gilding', hint: '1 leaf' },
    { id: 'double', label: 'Two variations', hint: '2 leaves' },
    { id: '4up', label: 'Four variations', hint: `${MODE_CREDITS['4up']} leaves` },
  ];
  return (
    <div className="variation-tray mt-4 flex flex-col items-center gap-3 px-2 py-4 sm:mt-6 sm:px-5">
      <p className="font-display italic text-caption text-[#f4e7c8]">How many gildings?</p>
      <div role="radiogroup" aria-label="How many images" className="grid w-full grid-cols-3 gap-1.5 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-center sm:gap-2">
        {modes.map((m) => {
          const active = value === m.id;
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(m.id)}
              className={cn(
                'variation-choice min-w-0 flex flex-col items-center gap-2 px-1 py-2 font-display text-[0.78rem] transition-colors duration-200 sm:min-w-32 sm:px-4 sm:text-caption',
                active
                  ? 'is-selected'
                  : '',
              )}
            >
              <span className="variation-medallion" aria-hidden="true">
                <span>{m.id === 'single' ? 'I' : m.id === 'double' ? 'II' : 'IV'}</span>
                {active && <span className="variation-seal">✓</span>}
              </span>
              <span className="block leading-tight text-center">{m.label}</span>
              <span className="variation-cost block italic leading-tight">{m.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================================
   Shape hanging frames — aspect ratios as actual gilt-frame rectangles
   at their true proportion, floating with a hair of parallax on hover.
   ==================================================================== */

type Aspect = '1:1' | '3:2' | '2:3' | '16:9' | '9:16';

const ASPECT_DEFS: ReadonlyArray<{ id: Aspect; w: number; h: number; caption: string }> = [
  { id: '1:1', w: 96, h: 96, caption: 'Square' },
  { id: '3:2', w: 112, h: 112 / 1.5, caption: 'Classic' },
  { id: '2:3', w: 112 / 1.5, h: 112, caption: 'Portrait' },
  { id: '16:9', w: 112, h: 63, caption: 'Widescreen' },
  { id: '9:16', w: 63, h: 112, caption: 'Phone' },
];

function ShapeGiltFrames({
  value,
  onChange,
}: {
  value: Aspect;
  onChange: (next: Aspect) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Aspect ratio"
      className="shape-options mt-4 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:justify-center sm:gap-4"
    >
      {ASPECT_DEFS.map((a) => {
        const active = value === a.id;
        return (
          <button
            key={a.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${a.id} ${a.caption}`}
            onClick={() => { tap(); onChange(a.id); }}
            className={cn("shape-choice group flex min-w-0 flex-col items-center gap-3 rounded-xl border px-1 py-3 transition-colors duration-200 sm:min-w-40", active && "is-selected")}
          >
            <span className="flex h-[140px] w-full items-center justify-center" aria-hidden>
            {/* An ACTUAL ornate gilt frame (the same asset the living-sculpt
                wears) at the option's true proportion. Selected lifts with a
                warm halo + brighter well; the rest sit quietly. */}
            <span
              className={cn(
                'gilt-frame relative block shrink-0 transition-[filter] duration-200',
                active
                  ? 'drop-shadow-[0_18px_36px_rgba(233,200,115,0.45)]'
                  : 'opacity-80 drop-shadow-[0_10px_22px_rgba(0,0,0,0.6)] group-hover:opacity-100',
              )}
              style={{
                ['--gilt-w' as string]: '9px',
                boxSizing: 'content-box',
                width: `calc(${a.w}px * var(--shape-scale, 1))`,
                height: `calc(${a.h}px * var(--shape-scale, 1))`,
              }}
            >
              <span
                aria-hidden
                className="block h-full w-full"
                style={{
                  background: active
                    ? 'radial-gradient(130% 100% at 50% 0%, rgba(255,232,180,0.3), rgba(9,8,12,0.9) 60%)'
                    : 'radial-gradient(130% 100% at 50% 0%, rgba(255,232,180,0.1), rgba(9,8,12,0.92) 60%)',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.6)',
                }}
              />
            </span>
            </span>
            <span className="font-display text-caption text-center">
              <span className="block text-base">{a.id}</span><span className="block italic opacity-85">{a.caption}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* =====================================================================
   Strike plate — the arrival card at the doorway. Uses the exact Landing
   plate + halo + rake vocabulary; the button is the app’s primary
   GildButton, wired to the parent’s durable-gild mutation.
   ==================================================================== */

function StrikePlate({
  summary,
  strikeLabel,
  strikeCredits,
  onStrike,
  busy,
  waiting,
  recovering,
  canStrike,
}: {
  summary: string;
  strikeLabel: string;
  strikeCredits: number;
  onStrike: () => void;
  busy: boolean;
  waiting: boolean;
  recovering: boolean;
  canStrike: boolean;
}) {
  const busyLabel = recovering ? 'Finishing' : waiting ? 'Rendering' : 'Gilding';
  const leaves = `${strikeCredits} ${strikeCredits === 1 ? 'leaf' : 'leaves'}`;
  const disabled = !canStrike || busy;
  // The altar's terminus is one solid, precious act. A SOLID molten-gold
  // button (not the translucent glass CTA — that washed out over the bright
  // copper plate) with dark ink, so it reads unmistakably at the base of the
  // altar no matter what's behind it.
  return (
    <div className="relative flex max-w-md flex-col items-center gap-3">
      <p className="rounded-xl border border-accent/30 bg-black/75 px-4 py-3 text-center font-display text-caption leading-relaxed text-[#f3e8d1]">{summary}</p>
      <button
        type="button"
        disabled={disabled}
        onClick={onStrike}
        aria-busy={busy}
        aria-label={busy ? busyLabel : `${strikeLabel}, ${leaves}`}
        className={cn(
          'strike-gold group relative inline-flex items-center gap-3 rounded-full py-3 pl-3 pr-6 font-display text-[1.0625rem] font-semibold not-italic leading-none text-[#2a1d06]',
          'transition-transform duration-200 active:scale-[0.985]',
          disabled && 'cursor-not-allowed',
        )}
      >
        {busy ? (
          <span className="flex items-center gap-2.5 px-3">
            <StrikeSpinner />
            <span className="strike-engrave">{busyLabel}…</span>
          </span>
        ) : (
          <>
            {/* recessed coin seal holding the struck-gold gleam — sets the
                button as a minted object, not a flat pill */}
            <span className="strike-seal" aria-hidden>
              <GleamGlyph size={15} />
            </span>
            <span className="strike-engrave flex items-baseline gap-2">
              {strikeLabel}
              <span aria-hidden className="h-3 w-px self-center bg-[#5e3f12]/45" />
              <span className="text-[0.94em] text-[#4a3208]/85">{leaves}</span>
            </span>
          </>
        )}
      </button>
      {!canStrike && (
        <p className="font-display italic text-caption text-ink/75 drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
          Add a line to gild.
        </p>
      )}
    </div>
  );
}

function StrikeSpinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
      className="motion-safe:animate-spin"
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}

/* =====================================================================
   Commission plate — the corner glyph that progressively illuminates as
   each beat is answered. A slender manuscript plate carrying: the numeral
   under way, an inked sketch line (once the sketch beat fills), a small
   character mark (once a character is chosen), a style rubric, an aspect
   frame, and finally the leaf-cost seal.
   ==================================================================== */

interface BeatFilled {
  character: boolean;
  sketch: boolean;
  look: boolean;
  shape: boolean;
}

/* =====================================================================
   Beat rail — the mobile quick-nav. A floating gilt pill above the app's
   bottom nav: prev/next chevrons, six beat dots (the active one carries
   its roman numeral), and a leaf-sealed "Gild" that skips straight to
   the Strike. Portalled to document.body so iOS Safari's transform-
   rooted `fixed` bug (the one that bit the sheet) can't reach it.
   ==================================================================== */

const BEAT_ORDER = ['threshold', 'character', 'sketch', 'look', 'shape', 'strike'] as const;
type BeatName = (typeof BEAT_ORDER)[number];

function BeatRail({
  summary,
  activeBeat,
  nodes,
  onJump,
}: {
  summary: string;
  activeBeat: number;
  nodes: boolean[];
  onJump: (beat: BeatName) => void;
}) {
  useEffect(() => { const beat = BEAT_ORDER[activeBeat]; if (beat && beat !== 'threshold') trackProduct(`step.${beat}`); }, [activeBeat]);
  if (typeof document === 'undefined') return null;
  const canPrev = activeBeat > 0;
  const canNext = activeBeat < BEAT_ORDER.length - 1;
  const go = (i: number): void => {
    const beat = BEAT_ORDER[Math.max(0, Math.min(BEAT_ORDER.length - 1, i))];
    if (beat) {
      tap();
      onJump(beat);
    }
  };
  const rail = (
    <nav
      aria-label="Beat navigation"
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 [@media(min-width:768px)_and_(min-height:640px)]:hidden"
      style={{ bottom: 'calc(4.9rem + env(safe-area-inset-bottom))' }}
    >
      <div
        className="pointer-events-auto flex flex-col rounded-2xl border border-accent/40 px-2 py-2 backdrop-blur-md"
        style={{
          background:
            'linear-gradient(180deg, rgba(24,20,14,0.92), rgba(10,8,12,0.94))',
          boxShadow:
            'inset 0 1px 0 rgba(255,246,218,0.16), inset 0 0 0 1px rgba(233,200,115,0.08), 0 18px 44px -14px rgba(0,0,0,0.95)',
        }}
      >
        <p className="mx-3 mb-1 border-b border-accent/20 pb-1.5 text-center font-display text-[11px] tracking-[0.12em] text-[#e9c873]">{activeBeat === 0 ? 'THE COMMISSION' : `${toRoman(activeBeat + 1)} · ${['', 'YOUR CHARACTER', 'YOUR SCENE', 'THE LOOK', 'THE FRAME', 'YOUR GILDING'][activeBeat]}`}</p>
        <span className="sr-only">{summary}</span>
        <div className="flex items-center justify-center gap-1">
        <button
          type="button"
          aria-label="Previous section"
          disabled={!canPrev}
          onClick={() => go(activeBeat - 1)}
          className="flex h-11 w-11 items-center justify-center rounded-full text-accent-soft transition-colors active:bg-accent/10 disabled:opacity-25"
        >
          <RailChevron dir="left" />
        </button>

        <div className="hidden items-center gap-0.5 px-0.5">
          {BEAT_ORDER.map((beat, i) => {
            const isActive = activeBeat === i;
            const lit = !!nodes[i];
            return (
              <button
                key={beat}
                type="button"
                aria-label={`Go to ${beat}`}
                aria-current={isActive ? 'step' : undefined}
                onClick={() => go(i)}
                className="flex h-9 items-center justify-center rounded-full transition-all duration-300"
                style={{ width: isActive ? '1.85rem' : '1.05rem' }}
              >
                {isActive ? (
                  <span
                    className="flex h-[1.55rem] min-w-[1.55rem] items-center justify-center rounded-full px-1 font-display text-[0.72rem] font-semibold italic leading-none text-[#3a2708]"
                    style={{
                      background: 'linear-gradient(180deg,#fff6da,#e9c873 44%,#bd8c2c)',
                      boxShadow:
                        '0 2px 6px -1px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.7), 0 0 12px rgba(233,200,115,0.4)',
                    }}
                  >
                    {toRoman(i + 1)}
                  </span>
                ) : (
                  <span
                    className={cn(
                      'block rounded-full transition-all duration-300',
                      lit
                        ? 'h-[7px] w-[7px] bg-accent shadow-[0_0_6px_rgba(233,200,115,0.7)]'
                        : 'h-[5px] w-[5px] bg-ink/30',
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          aria-label="Next section"
          disabled={!canNext}
          onClick={() => go(activeBeat + 1)}
          className="flex h-11 items-center justify-center gap-2 rounded-full px-4 font-display text-caption text-accent-soft transition-colors active:bg-accent/10 disabled:opacity-25"
        >
          Next
          <RailChevron dir="right" />
        </button>

        <span aria-hidden className="mx-1 h-6 w-px bg-gradient-to-b from-transparent via-accent/40 to-transparent" />

        <button
          type="button"
          aria-label="Review your gilding"
          onClick={() => go(BEAT_ORDER.length - 1)}
          className="flex h-11 items-center gap-1.5 rounded-full pl-3 pr-3.5 font-display text-[0.82rem] font-semibold not-italic leading-none text-[#2a1d06] transition-transform active:scale-95"
          style={{
            background: 'linear-gradient(180deg,#fff6da,#f0d488 40%,#d9a943)',
            boxShadow:
              '0 3px 10px -2px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.75), inset 0 -2px 4px rgba(120,80,20,0.3)',
          }}
        >
          <GleamGlyph size={13} /> Review
        </button>
      </div>
      </div>
    </nav>
  );
  return createPortal(rail, document.body);
}

function RailChevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 12 12"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={dir === 'left' ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path d="M4.5 2.5 L8 6 L4.5 9.5" />
    </svg>
  );
}

function CommissionPlate({
  activeBeat,
  filled,
  stylePreset,
  aspectRatio,
  credits,
  characterUrl,
  onJump,
}: {
  activeBeat: number;
  filled: BeatFilled;
  stylePreset: StylePreset;
  aspectRatio: Aspect;
  credits: number;
  characterUrl: string | null;
  /** Scroll the corridor to the centre of the given beat. */
  onJump: (beat: 'threshold' | 'character' | 'sketch' | 'look' | 'shape' | 'strike') => void;
}) {
  const aspectDef = ASPECT_DEFS.find((a) => a.id === aspectRatio);
  const aw = aspectDef ? Math.round(aspectDef.w * 0.35) : 20;
  const ah = aspectDef ? Math.round(aspectDef.h * 0.35) : 20;
  return (
    <aside
      aria-label="Commission navigation"
      className="pointer-events-auto absolute right-5 top-28 z-20 hidden w-60 flex-col gap-1 rounded-lg border border-accent/20 p-4 text-ink/85 backdrop-blur-md [@media(min-width:768px)_and_(min-height:640px)]:flex"
      style={{
        background: 'linear-gradient(180deg, rgba(24,20,14,0.7), rgba(10,8,12,0.78))',
        boxShadow:
          'inset 0 1px 0 rgba(255,246,218,0.14), inset 0 0 0 1px rgba(233,200,115,0.06), 0 28px 64px -24px rgba(0,0,0,0.92)',
      }}
    >
      {/* corner ticks — a framed-plate tell */}
      <span aria-hidden className="pointer-events-none absolute left-2 top-2 h-3 w-3 border-l border-t border-accent/45" />
      <span aria-hidden className="pointer-events-none absolute right-2 top-2 h-3 w-3 border-r border-t border-accent/45" />
      <span aria-hidden className="pointer-events-none absolute bottom-2 left-2 h-3 w-3 border-b border-l border-accent/45" />
      <span aria-hidden className="pointer-events-none absolute bottom-2 right-2 h-3 w-3 border-b border-r border-accent/45" />

      <div className="flex items-center justify-center gap-2 pb-1.5">
        <span aria-hidden className="h-px w-4 bg-gradient-to-r from-transparent to-accent/60" />
        <FleuronMark small />
        <span aria-hidden className="h-px w-4 bg-gradient-to-l from-transparent to-accent/60" />
      </div>
      <p className="mb-1.5 text-center font-mono text-[0.6rem] uppercase tracking-[0.3em] text-accent-soft">
        the commission
      </p>

      {/* I. Threshold — always "done" (it's the entrance) */}
      <PlateRow numeral="I" label="threshold" done activeBeat={activeBeat} thisBeat={0} onJump={() => onJump('threshold')}>
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent-soft/80" />
      </PlateRow>

      {/* II. Character */}
      <PlateRow numeral="II" label="character" done={filled.character} activeBeat={activeBeat} thisBeat={1} onJump={() => onJump('character')}>
        {characterUrl ? (
          <img
            src={characterUrl}
            alt=""
            className="h-6 w-6 shrink-0 rounded-full border border-accent/50 object-cover"
          />
        ) : (
          <span aria-hidden className="h-6 w-6 shrink-0 rounded-full border border-hairline/50" />
        )}
      </PlateRow>

      {/* III. Sketch */}
      <PlateRow numeral="III" label="sketch" done={filled.sketch} activeBeat={activeBeat} thisBeat={2} onJump={() => onJump('sketch')}>
        <span aria-hidden className="h-px w-6 shrink-0 rounded-full bg-gradient-to-r from-accent/80 to-accent/20" />
      </PlateRow>

      {/* IV. Look */}
      <PlateRow numeral="IV" label="look" done={filled.look} activeBeat={activeBeat} thisBeat={3} onJump={() => onJump('look')}>
        <span className="font-display text-[0.7rem] italic text-ink/70">
          {STYLE_PROFILES[stylePreset]?.label.split(' ')[0] ?? '·'}
        </span>
      </PlateRow>

      {/* V. Shape */}
      <PlateRow numeral="V" label="shape" done={filled.shape} activeBeat={activeBeat} thisBeat={4} onJump={() => onJump('shape')}>
        <span
          aria-hidden
          className="block shrink-0 rounded-[1px]"
          style={{
            width: `${Math.max(aw, 10)}px`,
            height: `${Math.max(ah, 10)}px`,
            background: 'linear-gradient(180deg,#fff6da,#c2922f 60%,#5e3f12)',
          }}
        />
      </PlateRow>

      {/* VI. Strike — the arrival plate. */}
      <PlateRow numeral="VI" label="strike" done={false} activeBeat={activeBeat} thisBeat={5} onJump={() => onJump('strike')}>
        <span
          aria-hidden
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-display text-[0.62rem] font-semibold not-italic leading-none text-[#3a2708]"
          style={{
            background: 'linear-gradient(180deg,#fff6da,#e9c873 45%,#bd8c2c)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.6)',
          }}
        >
          <LeafGlyph size={9} /> {credits}
        </span>
      </PlateRow>
    </aside>
  );
}

function PlateRow({
  numeral,
  label,
  done,
  activeBeat,
  thisBeat,
  onJump,
  children,
}: {
  numeral: string;
  label: string;
  done: boolean;
  activeBeat: number;
  thisBeat: number;
  onJump: () => void;
  children?: ReactNode;
}) {
  const isActive = activeBeat === thisBeat;
  return (
    <button
      type="button"
      onClick={() => { tap(); onJump(); }}
      aria-current={isActive ? 'step' : undefined}
      className={cn(
        'group flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-opacity duration-300 hover:bg-accent-glow/10',
        isActive && 'bg-accent-glow/10',
        done ? 'opacity-100' : 'opacity-45 hover:opacity-70',
      )}
    >
      <span
        className={cn(
          'w-6 shrink-0 font-display italic',
          isActive ? 'text-accent-soft' : done ? 'text-ink/85' : 'text-ink/55',
        )}
      >
        {numeral}.
      </span>
      <span
        className={cn(
          'w-14 shrink-0 font-mono text-[0.62rem] uppercase tracking-[0.14em] transition-colors',
          isActive ? 'text-accent-soft' : 'text-ink/60 group-hover:text-ink/80',
        )}
      >
        {label}
      </span>
      <span className="ml-auto flex items-center gap-2">{children}</span>
    </button>
  );
}

/* =====================================================================
   Small helpers — Halo, Fleuron, ScrollGlyph, LeafGlyph, GildWord.
   All bespoke SVG (matches the manuscript-ornament language of the app).
   ==================================================================== */

function Halo({ width = '160%', height = '180%' }: { width?: string; height?: string }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{
        width,
        height,
        background: 'radial-gradient(closest-side, rgba(0,0,0,0.62), rgba(0,0,0,0.3) 55%, transparent 78%)',
        filter: 'blur(8px)',
      }}
    />
  );
}

function FleuronMark({ small = false }: { small?: boolean }) {
  const w = small ? 20 : 34;
  const h = small ? 12 : 20;
  return (
    <svg
      viewBox="0 0 34 20"
      width={w}
      height={h}
      aria-hidden="true"
      className="mx-auto"
    >
      <defs>
        <linearGradient id="fleuron-scrub" x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#a87f2c" />
          <stop offset="0.42" stopColor="#e9c873" />
          <stop offset="0.52" stopColor="#fff3ce" />
          <stop offset="0.64" stopColor="#c2922f" />
          <stop offset="1" stopColor="#7d521a" />
        </linearGradient>
      </defs>
      <path d="M2 10 L17 4 L32 10 L17 16 Z" fill="url(#fleuron-scrub)" />
      <path d="M2 10 L32 10" stroke="#fff3ce" strokeWidth="0.4" opacity="0.65" />
      <circle cx="17" cy="10" r="1.3" fill="#fff3ce" />
    </svg>
  );
}

function ScrollGlyph() {
  return (
    <svg viewBox="0 0 14 22" width="13" height="20" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <rect x="1.5" y="1.5" width="11" height="19" rx="5.5" />
      <line x1="7" y1="6" x2="7" y2="9.5" strokeLinecap="round">
        <animate attributeName="y1" values="6;10;6" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="y2" values="9.5;13.5;9.5" dur="1.8s" repeatCount="indefinite" />
      </line>
    </svg>
  );
}

function LeafGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M12 2 C 7 5 5 10 5 14 c 0 3.5 2 6 4 7 c 0.5 -3.5 2.5 -8 5.5 -11 C 12 12 10.5 15 10 18 c 3.5 -1 6 -4 7.5 -8 C 18.5 6.5 16 3 12 2 Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** GleamGlyph — a four-point struck-gold spark with a companion glint: the
 *  "moment a sketch becomes gold" mark. The action symbol for the Gild
 *  buttons (the leaf reads as currency, which confused the CTA). */
function GleamGlyph({ size = 14 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12 1.6 c0.62 4.4 2.4 6.18 6.8 6.8 c-4.4 0.62 -6.18 2.4 -6.8 6.8 c-0.62 -4.4 -2.4 -6.18 -6.8 -6.8 c4.4 -0.62 6.18 -2.4 6.8 -6.8 Z" />
      <path d="M18.6 15 c0.26 1.6 0.86 2.2 2.46 2.46 c-1.6 0.26 -2.2 0.86 -2.46 2.46 c-0.26 -1.6 -0.86 -2.2 -2.46 -2.46 c1.6 -0.26 2.2 -0.86 2.46 -2.46 Z" opacity="0.85" />
    </svg>
  );
}

function GildWord({
  children,
  className,
  gated = false,
}: {
  children: string;
  className?: string;
  gated?: boolean;
}) {
  return (
    <span
      className={cn('gild-word', gated && 'gild-word--gated', className)}
      data-text={children}
    >
      {children}
    </span>
  );
}

/* =====================================================================
   Toast — a fixed card at the foot for errors, out-of-leaves, and
   backgrounded-gild recovery. Mirrors Landing’s pattern exactly.
   ==================================================================== */

function ToastCard({
  errorMessage,
  outOfLeaves,
  recovering,
  recoverHint,
}: {
  errorMessage: string | null;
  outOfLeaves: boolean;
  recovering: boolean;
  recoverHint: boolean;
}) {
  if (recovering) {
    return (
      <div
        className="max-w-md rounded-md border border-accent/40 bg-surface/95 px-5 py-4 shadow-2xl backdrop-blur"
        role="status"
        aria-live="polite"
      >
        <h2 className="font-display text-h-meta font-semibold text-accent">
          Finishing your gilding…
        </h2>
        <p className="mt-1 font-display italic text-caption text-ink-soft">
          Your screen slept mid-gild, but it kept working. Fetching the finished piece now.
        </p>
      </div>
    );
  }
  if (recoverHint) {
    return (
      <div className="max-w-md rounded-md border border-accent/40 bg-surface/95 px-5 py-4 shadow-2xl backdrop-blur" role="status">
        <h2 className="font-display text-h-meta font-semibold text-accent">
          Your gilding is still finishing
        </h2>
        <p className="mt-1 font-display italic text-caption text-ink-soft">
          It’s saving to your gallery. Give it a moment, then take a look. No leaves were wasted.
        </p>
      </div>
    );
  }
  if (errorMessage) {
    return (
      <div className="max-w-md rounded-md border border-danger/30 bg-surface/95 px-5 py-4 shadow-2xl backdrop-blur" role="alert">
        <h2 className="font-display text-h-meta font-semibold text-danger">
          {outOfLeaves ? 'Out of leaves' : 'Something didn’t take'}
        </h2>
        <p className="mt-1 font-display italic text-caption text-ink-soft">{errorMessage}</p>
      </div>
    );
  }
  return null;
}
